import React, {createContext, useContext, useEffect, useState} from 'react';
import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
} from 'firebase/remote-config';
import {auth} from '@src/firebase';

interface FirebaseRemoteConfigContextType {
  anonymousAuthEnabled: boolean;
  isLoading: boolean;
}

const FirebaseRemoteConfigContext =
  createContext<FirebaseRemoteConfigContextType>({
    anonymousAuthEnabled: false,
    isLoading: true,
  });

export const useFirebaseRemoteConfig = () => {
  const context = useContext(FirebaseRemoteConfigContext);
  if (!context) {
    throw new Error(
      'useFirebaseRemoteConfig must be used within FirebaseRemoteConfigProvider',
    );
  }
  return context;
};

interface FirebaseRemoteConfigProviderProps {
  children: React.ReactNode;
}

const FirebaseRemoteConfigProvider: React.FC<
  FirebaseRemoteConfigProviderProps
> = ({children}) => {
  const [anonymousAuthEnabled, setAnonymousAuthEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initRemoteConfig = async () => {
      try {
        const remoteConfig = getRemoteConfig(auth.app);

        // Configuration par défaut
        remoteConfig.defaultConfig = {
          anonymous_auth: false,
        };

        // Paramètres de fetch
        remoteConfig.settings = {
          minimumFetchIntervalMillis: 300000, // 5 minutes
          fetchTimeoutMillis: 60000, // 1 minute
        };

        // If we're running on localhost / emulator, skip the network fetch.
        // Remote Config doesn't have a stable emulator endpoint in the browser SDK,
        // so fetchAndActivate triggers network calls (Installations API) which
        // can return 403 when working with local emulators or missing credentials.
        const isLocalhost =
          typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname === '0.0.0.0');

        if (
          isLocalhost ||
          process.env.GATSBY_FIREBASE_USE_EMULATOR === 'true'
        ) {
          // Use default config when running locally/emulator to avoid external API calls
          const defaultAnonymous =
            (remoteConfig as any).defaultConfig?.anonymous_auth ?? false;
          setAnonymousAuthEnabled(Boolean(defaultAnonymous));
          console.info(
            '⚠️ Skipping Firebase Remote Config fetch in local/emulator mode - using default anonymous_auth:',
            defaultAnonymous,
          );
        } else {
          // Fetch et activation des nouvelles valeurs
          await fetchAndActivate(remoteConfig);

          // Récupération de la valeur
          const value = getValue(remoteConfig, 'anonymous_auth');
          setAnonymousAuthEnabled(value.asBoolean());

          console.log(
            '✅ Firebase Remote Config - anonymous_auth:',
            value.asBoolean(),
          );
        }
      } catch (error) {
        console.error(
          "❌ Erreur lors de l'initialisation de Firebase Remote Config:",
          error,
        );
        // Utiliser la valeur par défaut en cas d'erreur
        setAnonymousAuthEnabled(false);
      } finally {
        setIsLoading(false);
      }
    };

    initRemoteConfig();
  }, []);

  return (
    <FirebaseRemoteConfigContext.Provider
      value={{anonymousAuthEnabled, isLoading}}>
      {children}
    </FirebaseRemoteConfigContext.Provider>
  );
};

export default FirebaseRemoteConfigProvider;
